/**
 * Terms Acceptance Modal
 *
 * Displayed as a full-screen overlay when a user's accepted legal document versions
 * are behind the current platform versions. Cannot be dismissed without accepting.
 */
import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Loader2, FileText, Shield } from 'lucide-react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { coreAPI, type LegalContent } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

export default function TermsAcceptanceModal() {
  const { user, checkAuthStatus } = useAuth();
  const [legalContent, setLegalContent] = useState<LegalContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>('terms');

  // Determine what needs acceptance
  const needsTermsAcceptance = user
    && (user.terms_accepted_version ?? 0) < (user.terms_current_version ?? 1);
  const needsPrivacyAcceptance = user
    && (user.privacy_accepted_version ?? 0) < (user.privacy_current_version ?? 1);
  const needsAcceptance = needsTermsAcceptance || needsPrivacyAcceptance;

  useEffect(() => {
    if (!needsAcceptance) return;

    coreAPI.getLegalContent()
      .then((res) => setLegalContent(res.data))
      .catch(() => {
        // If we can't fetch legal content, don't block the user
        toast.error('Failed to load legal documents');
      })
      .finally(() => setLoading(false));
  }, [needsAcceptance]);

  const handleAccept = useCallback(async () => {
    if (!user || !legalContent) return;

    setSubmitting(true);
    try {
      const payload: Record<string, number> = {};
      if (needsTermsAcceptance) {
        payload['terms_version'] = legalContent.terms_version;
      }
      if (needsPrivacyAcceptance) {
        payload['privacy_version'] = legalContent.privacy_version;
      }

      await coreAPI.acceptLegal(payload);

      // Refresh the auth status to get updated acceptance versions
      await checkAuthStatus();

      toast.success('Legal documents accepted');
    } catch (error) {
      console.error('Failed to accept legal documents:', error);
      toast.error('Failed to accept. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [user, legalContent, needsTermsAcceptance, needsPrivacyAcceptance, checkAuthStatus]);

  // Don't render if no acceptance needed
  if (!needsAcceptance) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            Updated Legal Documents
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Please review and accept the updated documents to continue using the platform.
          </p>
        </div>

        {/* Tab Selector */}
        {needsTermsAcceptance && needsPrivacyAcceptance && (
          <div className="px-6 pt-3 flex-shrink-0">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('terms')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'terms'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <FileText className="h-4 w-4" />
                Terms & Conditions
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('privacy')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'privacy'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Shield className="h-4 w-4" />
                Privacy Policy
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : legalContent ? (
            <div className="prose prose-sm prose-gray max-w-none">
              {(activeTab === 'terms' && needsTermsAcceptance) ? (
                legalContent.terms_content ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(legalContent.terms_content) }} />
                ) : (
                  <p className="text-gray-500 italic">
                    Terms & Conditions content has been updated. Please accept to continue.
                  </p>
                )
              ) : (
                legalContent.privacy_content ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(legalContent.privacy_content) }} />
                ) : (
                  <p className="text-gray-500 italic">
                    Privacy Policy content has been updated. Please accept to continue.
                  </p>
                )
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Legal documents have been updated. Please accept to continue.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex-shrink-0 space-y-3">
          <div className="flex items-start gap-3">
            <Checkbox
              id="accept-legal"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
              className="mt-0.5"
            />
            <label htmlFor="accept-legal" className="text-sm text-gray-700 cursor-pointer select-none">
              I have read and accept the{' '}
              {needsTermsAcceptance && 'Terms & Conditions'}
              {needsTermsAcceptance && needsPrivacyAcceptance && ' and '}
              {needsPrivacyAcceptance && 'Privacy Policy'}
            </label>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleAccept}
              disabled={!accepted || submitting}
              size="lg"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
